/**
 * Authentication & permissions store.
 *
 * Mirrors the monolith's `loadUserInfo` + `loadUserPermissions` flow, but
 * retargeted for Omni Cloud's MSAL Bearer-token auth (2026-07 adoption):
 *  1. Require an active MSAL account (App.jsx/main.jsx handle the sign-in
 *     redirect before this ever bootstraps) — this is the source of truth
 *     for "is anyone signed in", not a session cookie.
 *  2. GET /api/user-info (Bearer-authenticated) → user object + role.
 *     TODO: role is currently hardcoded 'admin' server-side for every
 *     caller until the real permissions store lands — see
 *     apps/api/src/routes/user-info.ts.
 *  3. If role === 'admin' → grant ADMIN_PERMISSIONS hardcoded list
 *     else → GET /api/user-permissions/:id → server-stored list (currently
 *     a compat stub returning all permissions — same TODO as above).
 *  4. Expose `hasPermission(id)` for permission gates.
 */

import { create } from 'zustand';
import {
  ADMIN_PERMISSIONS,
  fetchUserInfo,
  fetchUserPermissions
} from '../api/endpoints/auth.js';
import { resolveAccount } from '../auth/msal.js';
// One implementation of grant parsing, shared with the API's gates — a second
// copy here would drift, and a bare grant meaning anything other than "full"
// would silently change what every existing operator can do.
import { tierFor } from '@omni-itm/shared';

/**
 * @typedef {import('../api/endpoints/auth.js').UserInfo} UserInfo
 */

/**
 * @typedef {Object} AuthState
 * @property {UserInfo | null} user
 * @property {readonly string[]} permissions
 * @property {boolean} isAdmin
 * @property {boolean} loading
 * @property {string | null} error
 * @property {boolean} initialised
 * @property {() => Promise<void>} bootstrap
 * @property {(permissionId: string) => boolean} hasPermission
 * @property {() => void} reset
 */

/** @type {import('zustand').UseBoundStore<import('zustand').StoreApi<AuthState>>} */
export const useAuthStore = create((set, get) => ({
  user: null,
  permissions: [],
  isAdmin: false,
  loading: false,
  error: null,
  initialised: false,
  /**
   * Which feeds the SERVER monitors, from /api/user-info.
   *
   * Defaults to all-false, which is the safe direction: if the server does not
   * say, the browser monitor keeps doing exactly what it always did. Only an
   * explicit true stands it down, so the worst case is a duplicate toast rather
   * than no alerting at all.
   */
  serverMonitoring: { network: false, security: false, webFiltering: false },

  async bootstrap() {
    // Guard against double-bootstrapping (StrictMode mounts twice in dev).
    if (get().loading || get().initialised) return;

    set({ loading: true, error: null });
    try {
      // resolveAccount() also adopts an account MSAL holds but has not yet
      // promoted to active — bootstrap() runs before LoginGate's effect (child
      // effects run first), so getActiveAccount() alone raced and threw here.
      const account = resolveAccount();
      if (!account) {
        throw new Error('No active MSAL session');
      }

      const envelope = await fetchUserInfo();
      if (!envelope.authenticated || !envelope.user) {
        throw new Error('User not authenticated');
      }

      // Merge in MSAL account claims as a display fallback — the API's
      // decoded-token fields already cover this, but this keeps the store
      // resilient if /api/user-info ever trims its response.
      const user = {
        ...envelope.user,
        displayName: envelope.user.displayName || account.name || account.username,
        upn: envelope.user.upn || account.username
      };
      const isAdmin = (user.role || '').toLowerCase() === 'admin';

      /** @type {string[]} */
      let permissions;
      if (isAdmin) {
        permissions = [...ADMIN_PERMISSIONS];
      } else {
        try {
          permissions = await fetchUserPermissions(user.id);
        } catch (permErr) {
          // Match monolith fallback: if permissions can't load, grant none.
          console.error('Failed to load custom permissions:', permErr);
          permissions = [];
        }
      }

      set({
        user,
        permissions: Object.freeze(permissions),
        isAdmin,
        serverMonitoring: {
          network: envelope?.serverMonitoring?.network === true,
          security: envelope?.serverMonitoring?.security === true,
          webFiltering: envelope?.serverMonitoring?.webFiltering === true
        },
        loading: false,
        initialised: true,
        error: null
      });
    } catch (err) {
      set({
        user: null,
        permissions: [],
        isAdmin: false,
        loading: false,
        initialised: true,
        error: err instanceof Error ? err.message : 'Failed to load user'
      });
    }
  },

  /**
   * Does this person have ANY access to a tab — read, restricted or full?
   *
   * This is what shows the tab in the sidebar and lets its routes load, so it
   * must be tier-blind. A raw `permissions.includes(permissionId)` fails for a
   * tiered grant, because `user-management:read` is not the string
   * `user-management` — so granting somebody read or restricted access hid the
   * tab from them entirely, and only full made it appear.
   *
   * Exactly the defect requirePermission had on the API side, in the frontend
   * copy: the gate that decides IF you reach a tab must not also try to decide
   * HOW MUCH you may do there. That second question is requireTier's, per route.
   */
  hasPermission(permissionId) {
    const { isAdmin, permissions } = get();
    if (isAdmin) return true;
    return tierFor(permissions, permissionId) !== null;
  },

  /**
   * The tier held on a tab: 'read' | 'restricted' | 'full', or null for none.
   * Admins hold full on everything, matching every gate on the API side.
   *
   * For components that need to show or hide an ACTION rather than a tab. Note
   * the server is still the authority — this is for affordances, so a control
   * the caller cannot use is disabled with a reason rather than dead.
   */
  tierForTab(permissionId) {
    const { isAdmin, permissions } = get();
    if (isAdmin) return 'full';
    return tierFor(permissions, permissionId);
  },

  reset() {
    set({
      user: null,
      permissions: [],
      isAdmin: false,
      loading: false,
      error: null,
      initialised: false
    });
  }
}));
